import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, ChevronDown, BadgeCheck, ExternalLink, ChevronLeft, ChevronRight, Truck, Clock, ShieldCheck, PoundSterling } from "lucide-react";
import { useTrustpilotStats, formatRelativeTime } from "@/hooks/useTrustpilotStats";
import { useState, useRef, useEffect, TouchEvent } from "react";
import { HERO_COPY, currentSearchTheme, type SearchTheme } from "@/lib/searchTheme";
import NhsPill from "./NhsPill";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TrustpilotStars = ({ rating, size = 4 }: { rating: number; size?: number }) => {
  const starSize = size * 4;
  return (
    <div className="flex gap-[2px]">
      {[...Array(5)].map((_, i) => {
        const fillPercent = Math.min(100, Math.max(0, (rating - i) * 100));
        const isFull = fillPercent >= 100;
        return (
          <div key={i} className="relative flex-shrink-0" style={{ width: starSize, height: starSize }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 96 96" preserveAspectRatio="xMidYMid meet">
              <rect fill="#DCDCE6" width="96" height="96" />
              <path d="M48,64.7 L62.6,61 L68.7,79.8 L48,64.7 Z M81.6,40.4 L55.9,40.4 L48,16.2 L40.1,40.4 L14.4,40.4 L35.2,55.4 L27.3,79.6 L48.1,64.6 L60.9,55.4 L81.6,40.4 Z" fill="#FFFFFF" />
            </svg>
            {fillPercent > 0 && (
              <div className="absolute top-0 left-0 h-full overflow-hidden" style={{ width: isFull ? '100%' : `${fillPercent}%` }}>
                <svg className="h-full" viewBox="0 0 96 96" preserveAspectRatio="xMinYMid slice" style={{ width: starSize }}>
                  <rect fill="#00B67A" width="96" height="96" />
                  <path d="M48,64.7 L62.6,61 L68.7,79.8 L48,64.7 Z M81.6,40.4 L55.9,40.4 L48,16.2 L40.1,40.4 L14.4,40.4 L35.2,55.4 L27.3,79.6 L48.1,64.6 L60.9,55.4 L81.6,40.4 Z" fill="#FFFFFF" />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Hero = () => {
  const { reviewCount, rating, reviews, loading } = useTrustpilotStats();
  const [isExpanded, setIsExpanded] = useState(false);
  const [freeOpen, setFreeOpen] = useState(false);
  const [currentReview, setCurrentReview] = useState(0);
  const [selectedReviewForDialog, setSelectedReviewForDialog] = useState<number | null>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const userToggledRef = useRef(false);
  // Headline echoes the Google Ads keyword theme (?kw=...). Starts on the
  // default so the first render matches the prerendered HTML, then swaps.
  const [theme, setTheme] = useState<SearchTheme>("default");
  useEffect(() => {
    setTheme(currentSearchTheme());
  }, []);
  const copy = HERO_COPY[theme];

  const handleTouchStart = (e: TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e: TouchEvent) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setCurrentReview((p) => (p + 1) % reviews.length);
      else setCurrentReview((p) => (p - 1 + reviews.length) % reviews.length);
    }
  };

  const features = [
    { icon: Clock, label: "5 to 10 min call" },
    { icon: Truck, label: "1 to 3 day UK delivery" },
    { icon: ShieldCheck, label: "Skip GP & pharmacy queues" },
    { icon: PoundSterling, label: "100% free on the NHS" },
  ];

  return (
    <section className="relative overflow-hidden bg-hero-bg">
      <div className="absolute inset-0 z-0 bg-hero-bg" />

      {/* ========== MOBILE LAYOUT ========== */}
      <div className="lg:hidden relative z-10 px-6 pt-24 pb-6 min-h-[100vh] flex flex-col">
        <div className="mb-4">
          <NhsPill />
        </div>

        <div className="mt-auto">
          <h1 className="text-[1.8rem] sm:text-4xl font-bold text-navy leading-[1.1] tracking-tight mt-4 mb-0 pb-1 animate-fade-up relative z-20">
            {copy.line1}
            <br />
            <span className="text-primary">{copy.line2}</span>
          </h1>

          {/* Hero image */}
          <div className="relative w-[calc(100%+3rem)] -mx-6 -mt-4 -mb-8 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-hero-bg to-transparent z-0 pointer-events-none" />
            <picture>
              <source media="(min-width: 1024px)" srcSet="/hero-bg-desktop.webp" />
              <img src="/hero-bg-mobile.webp" alt="Contraception Direct package" className="w-full h-auto object-contain mx-auto" width={750} height={500} loading="eager" fetchPriority="high" />
            </picture>
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-hero-bg to-transparent z-0 pointer-events-none" />
          </div>

          <div className="mb-8 animate-fade-up relative z-20" style={{ animationDelay: '0.05s' }}>
            <div className="text-[17px] sm:text-xl text-navy/70 leading-relaxed">
              {theme === "default" ? (
                <>
                  <p>The smart way to get your contraception.</p>
                  <p>Hassle free and clinically reviewed.</p>
                </>
              ) : (
                <p>{copy.sub}</p>
              )}
              <div className="flex justify-end mt-2">
                <button onClick={() => setFreeOpen(!freeOpen)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-coral bg-coral/10 hover:bg-coral/20 px-2.5 py-1 rounded-full transition-colors shrink-0 leading-none">
                  How can it be free?
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${freeOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
            <div className={`overflow-hidden transition-all duration-500 ease-out ${freeOpen ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
              <div className="bg-coral/10 backdrop-blur-sm rounded-2xl shadow-sm p-4 max-w-[340px] border border-coral/15">
                <p className="text-sm text-navy/70 leading-relaxed text-center">
                  The NHS Pharmacy Contraception Service was launched to make contraception easier to access, without needing a GP appointment.{' '}
                  We operate from Pharmacy Services Direct under this service, so your consultation and contraception are completely free on the NHS and we post it out to you at no cost.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-1 mb-8 animate-fade-up" style={{ animationDelay: '0.25s' }}>
            {features.slice(0, 3).map((item) => (
              <div key={item.label} className="inline-flex items-center gap-3 text-navy">
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                <span className="text-base font-medium">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="flex justify-center animate-fade-up mb-6" style={{ animationDelay: '0.35s' }}>
            <a href="#is-this-for-me">
              <Button variant="cta" size="pillLg" className="group h-auto flex-row items-center gap-2 text-lg px-5 py-2 transition-all duration-300">
                <Phone className="w-7 h-7" />
                <div className="flex flex-col">
                  <span>Start Now</span>
                  <span className="text-xs opacity-80 font-semibold">No account needed</span>
                </div>
              </Button>
            </a>
          </div>

          {/* Mobile Trustpilot */}
          <div>
            <div className="text-center mt-6 mb-1.5 animate-fade-up" style={{ animationDelay: '0.15s' }}>
              <p className="text-sm text-navy/60 font-medium whitespace-nowrap">8,000+ users and counting</p>
            </div>
            <div className="flex justify-center mb-6 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className={`bg-white/90 backdrop-blur-sm shadow-sm transition-all duration-700 ${isExpanded ? 'rounded-2xl shadow-lg w-full max-w-[90vw]' : 'rounded-xl w-full max-w-[90vw]'}`}>
                <button onClick={() => { userToggledRef.current = true; setIsExpanded(!isExpanded); }} className={`w-full inline-flex items-center gap-3 px-4 py-2.5 transition-all duration-300 hover:bg-white/100 ${isExpanded ? 'justify-center border-b border-navy/10 rounded-t-2xl' : 'rounded-xl'}`}>
                  <img src="https://cdn.trustpilot.net/brand-assets/4.1.0/logo-black.svg" alt="Trustpilot" className="h-4" loading="lazy" />
                  <div className="w-px h-4 bg-navy/20" />
                  <TrustpilotStars rating={rating ?? 4.8} size={4} />
                  <span className="text-sm font-semibold text-navy">{(rating ?? 4.8).toFixed(1)}</span>
                  <span className="text-xs text-navy/60">({reviewCount ? reviewCount.toLocaleString() : '…'})</span>
                  <ChevronDown className={`w-4 h-4 text-navy/40 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-700 ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="p-4 pt-3">
                    {reviews.length > 0 && (
                      <div className="relative min-h-[180px] touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
                        {reviews.map((review, idx) => (
                          <div key={idx} className={`absolute inset-0 transition-all duration-500 ${idx === currentReview ? 'opacity-100 translate-x-0' : idx < currentReview ? 'opacity-0 -translate-x-full' : 'opacity-0 translate-x-full'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <TrustpilotStars rating={review.rating} size={3} />
                              {review.verified && (<span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded-full"><BadgeCheck className="w-3 h-3" /> Verified</span>)}
                            </div>
                            <h4 className="font-semibold text-navy text-sm mb-1">{review.title}</h4>
                            <p className="text-navy/70 text-xs leading-relaxed line-clamp-5">{review.text}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs font-medium text-navy">{review.author}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-navy/50">{formatRelativeTime(review.date)}</span>
                                {review.url && (<a href={review.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-navy/40 hover:text-primary transition-colors" aria-label="View on Trustpilot"><ExternalLink className="w-3 h-3" /></a>)}
                              </div>
                            </div>
                            {idx === currentReview && review.text.length > 200 && (
                              <button onClick={(e) => { e.stopPropagation(); setSelectedReviewForDialog(idx); }} className="text-[10px] text-primary font-medium mt-2 mb-4 hover:underline w-full text-center py-2 touch-manipulation">Read more</button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-center gap-1.5 mt-6 pt-3 border-t border-navy/5">
                      {reviews.map((_, idx) => (<button key={idx} onClick={() => setCurrentReview(idx)} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${idx === currentReview ? 'bg-primary w-4' : 'bg-navy/20'}`} />))}
                    </div>
                    <a href="https://uk.trustpilot.com/review/contraceptiondirect.uk" target="_blank" rel="noopener noreferrer" className="block text-center text-xs text-primary font-medium mt-3 hover:underline">View all reviews on Trustpilot →</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== DESKTOP LAYOUT ========== */}
      <div className="hidden lg:block relative z-10 px-10 xl:px-16 pt-20 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-8 xl:gap-12 items-center">
            {/* Left column */}
            <div className="col-span-7 flex flex-col">
              {/* NHS pill - compact eyebrow */}
              <div className="mb-5 animate-fade-up">
                <NhsPill align="left" />
              </div>

              <h1 className="text-[3rem] xl:text-[3.75rem] font-bold text-navy leading-[1.05] tracking-tight pb-1 animate-fade-up">
                {copy.line1}
                <br />
                <span className="text-primary">{copy.line2}</span>
              </h1>

              <div className="mt-4 animate-fade-up" style={{ animationDelay: '0.05s' }}>
                <p className="text-lg xl:text-xl text-navy/70 leading-relaxed max-w-lg">
                  {copy.sub}
                </p>
                <div className="mt-3">
                  <button onClick={() => setFreeOpen(!freeOpen)} className="inline-flex items-center gap-1 text-xs font-semibold text-coral bg-coral/10 hover:bg-coral/20 px-3 py-1.5 rounded-full transition-colors">
                    How can it be free?
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${freeOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-500 ease-out ${freeOpen ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                    <div className="bg-coral/10 backdrop-blur-sm rounded-2xl p-4 max-w-lg border border-coral/15">
                      <p className="text-sm text-navy/70 leading-relaxed">
                        The NHS Pharmacy Contraception Service was launched to make contraception easier to access, without needing a GP appointment. We operate from Pharmacy Services Direct under this service, so your consultation and contraception are completely free on the NHS and we post it out to you at no cost.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature 2x2 grid */}
              <div className="mt-6 grid grid-cols-2 gap-y-3 gap-x-6 max-w-lg animate-fade-up" style={{ animationDelay: '0.2s' }}>
                {features.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                        <Icon className="w-[18px] h-[18px]" strokeWidth={2.25} />
                      </div>
                      <span className="text-navy font-semibold text-[14px]">{item.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* CTA + Trustpilot row */}
              <div className="mt-7 flex flex-wrap items-start gap-4 animate-fade-up" style={{ animationDelay: '0.25s' }}>
                <a href="#is-this-for-me">
                  <Button variant="cta" size="pillLg" className="group h-auto flex-row items-center gap-3 text-lg px-6 py-3.5 shadow-cta hover:shadow-glow transition-all duration-300">
                    <Phone className="w-6 h-6" />
                    <div className="flex flex-col text-left">
                      <span className="leading-tight">Start Now</span>
                      <span className="text-xs opacity-80 font-semibold leading-tight">No account needed</span>
                    </div>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </a>

                <div className={`bg-white/90 backdrop-blur-sm shadow-sm transition-all duration-500 ${isExpanded ? 'rounded-2xl shadow-lg w-[340px]' : 'rounded-xl w-[320px]'}`}>
                  <button onClick={() => { userToggledRef.current = true; setIsExpanded(!isExpanded); }} className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 transition-all duration-300 hover:bg-white ${isExpanded ? 'border-b border-navy/10 rounded-t-2xl' : 'rounded-xl'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <img src="https://cdn.trustpilot.net/brand-assets/4.1.0/logo-black.svg" alt="Trustpilot" className="h-4 shrink-0" loading="lazy" />
                      <div className="w-px h-4 bg-navy/20 shrink-0" />
                      <TrustpilotStars rating={rating ?? 4.8} size={3.5} />
                      <span className="text-sm font-semibold text-navy">{(rating ?? 4.8).toFixed(1)}</span>
                      <span className="text-xs text-navy/60">({reviewCount ? reviewCount.toLocaleString() : '…'})</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-navy/50 shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="p-4 pt-3">
                      {reviews.length > 0 && reviews[currentReview] && (
                        <div className="min-h-[140px]">
                          <div className="flex items-center gap-2 mb-2">
                            <TrustpilotStars rating={reviews[currentReview].rating} size={3.5} />
                            {reviews[currentReview].verified && (<span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded-full"><BadgeCheck className="w-3 h-3" /> Verified</span>)}
                          </div>
                          <h4 className="font-semibold text-navy text-sm mb-1">{reviews[currentReview].title}</h4>
                          <p className="text-navy/70 text-xs leading-relaxed line-clamp-4">{reviews[currentReview].text}</p>
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-navy/5">
                            <span className="text-xs font-medium text-navy truncate pr-2">{reviews[currentReview].author} · <span className="text-navy/50 font-normal">{formatRelativeTime(reviews[currentReview].date)}</span></span>
                            <div className="flex items-center gap-2 shrink-0">
                              <button onClick={() => setCurrentReview((currentReview - 1 + reviews.length) % reviews.length)} className="w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center"><ChevronLeft className="w-3 h-3 text-navy/60" /></button>
                              <button onClick={() => setCurrentReview((currentReview + 1) % reviews.length)} className="w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center"><ChevronRight className="w-3 h-3 text-navy/60" /></button>
                            </div>
                          </div>
                        </div>
                      )}
                      <a href="https://uk.trustpilot.com/review/contraceptiondirect.uk" target="_blank" rel="noopener noreferrer" className="block text-center text-xs text-primary font-medium mt-3 hover:underline">View all reviews on Trustpilot →</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: framed image */}
            <div className="col-span-5 relative animate-fade-up" style={{ animationDelay: '0.1s' }}>
              <div className="absolute -top-10 -right-8 w-64 h-64 bg-hero-bg rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-10 -left-6 w-48 h-48 bg-hero-bg rounded-full blur-3xl pointer-events-none" />

              <div className="relative w-full max-w-[640px] mx-auto">
                <img
                  src="/hero-bg-desktop.webp"
                  alt="Contraception Direct package"
                  className="w-full h-auto object-contain"
                  width={721}
                  height={481}
                  loading="eager"
                  fetchPriority="high"
                />

                {/* Floating stat badge */}
                <div className="absolute bottom-4 right-2 xl:right-6 bg-navy text-white px-4 py-2.5 rounded-2xl shadow-xl flex flex-col">
                  <span className="text-lg font-bold leading-tight">8,000+</span>
                  <span className="text-[10px] text-white/70 font-medium uppercase tracking-wider">Users served</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Dialog (shared) */}
      <Dialog open={selectedReviewForDialog !== null} onOpenChange={(open) => !open && setSelectedReviewForDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          {selectedReviewForDialog !== null && reviews[selectedReviewForDialog] && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <TrustpilotStars rating={reviews[selectedReviewForDialog].rating} size={4} />
                  {reviews[selectedReviewForDialog].verified && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded-full">
                      <BadgeCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <DialogTitle className="text-navy text-base">{reviews[selectedReviewForDialog].title}</DialogTitle>
              </DialogHeader>
              <p className="text-navy/70 text-sm leading-relaxed">{reviews[selectedReviewForDialog].text}</p>
              <div className="flex items-center justify-between mt-2 pt-3 border-t border-navy/10">
                <div>
                  <span className="text-sm font-medium text-navy">{reviews[selectedReviewForDialog].author}</span>
                  <span className="text-xs text-navy/50 ml-2">{formatRelativeTime(reviews[selectedReviewForDialog].date)}</span>
                </div>
                <a href={reviews[selectedReviewForDialog].url || "https://uk.trustpilot.com/review/contraceptiondirect.uk"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary font-medium hover:underline">
                  {reviews[selectedReviewForDialog].url ? "View this review" : "See all reviews"}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Hero;
